import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { AppState } from "../../store/root.reducer";
import { ResetErrorMsg, SigninStart } from "./store/auth.actions";

import { Input, Form } from "../../components/molecules/Form";
import Heading from "../../components/atoms/Heading";
import styles from '../../components/modals/modal.module.css';

export const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const auth = useSelector((state: AppState) => state.auth);

  const [email, setEmail] = React.useState<string>("");
  const [password, setPassword] = React.useState<string>("");
  
  // Track if fields have been modified to hide errors on typing
  const [emailTouched, setEmailTouched] = React.useState<boolean>(false);
  const [passwordTouched, setPasswordTouched] = React.useState<boolean>(false);

  // Parse auth errors for field-specific display
  const getFieldError = (fieldName: string) => {
    if (!auth.authError) return null;
    
    // Hide errors if user has started typing in the field
    if (fieldName === 'email' && emailTouched) return null;
    if (fieldName === 'password' && passwordTouched) return null;
    
    // Check if auth.authError is already an object
    if (typeof auth.authError === 'object') {
      const fieldError = auth.authError[fieldName];
      
      // Ensure we return a string, not an object
      if (fieldError) {
        if (typeof fieldError === 'string') {
          return fieldError;
        } else if (typeof fieldError === 'object') {
          // If it's an object, try to extract message or convert to string
          return fieldError.message || fieldError.error || JSON.stringify(fieldError);
        }
        return String(fieldError);
      }
      return null;
    }
    
    // If it's a string, try to parse as JSON
    if (typeof auth.authError === 'string') {
      try {
        // Try to parse as JSON object for field-specific errors
        const errorObj = JSON.parse(auth.authError);
        const fieldError = errorObj[fieldName];
        
        // Ensure we return a string, not an object
        if (fieldError) {
          if (typeof fieldError === 'string') {
            return fieldError;
          } else if (typeof fieldError === 'object') {
            // If it's an object, try to extract message or convert to string
            return fieldError.message || fieldError.error || JSON.stringify(fieldError);
          }
          return String(fieldError);
        }
        return null;
      } catch {
        // If not JSON, treat as general error for email field (most common case)
        if (fieldName === 'email') {
          return auth.authError;
        }
        return null;
      }
    }
    
    return null;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!auth.loading) dispatch(SigninStart({ navigate, email, password }));
  };

  React.useEffect(() => {
    dispatch(ResetErrorMsg());
  }, []);

  // Reset touched states when a new error comes in
  React.useEffect(() => {
    if (auth.authError) {
      setEmailTouched(false);
      setPasswordTouched(false);
    }
  }, [auth.authError]);

  // Custom onChange handlers that track touched state
  const handleEmailChange = (value: string) => {
    setEmail(value);
    setEmailTouched(true);
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    setPasswordTouched(true);
  };

  return (
    <>
      <div className="container mt-2">
        <Heading h2 className="mb-1">
          Login
        </Heading>
        <Form onSubmit={onSubmit}>
          <Input
            type="text"
            autocomplete="username"
            required
            title="Email address"
            placeholder="Your email"
            state={{
              error: getFieldError('email') ? {
                condition: true,
                text: getFieldError('email'),
              } : null,
              details: !getFieldError('email') ? {
                condition: true,
                text: "Type your user name or email",
              } : null,
            }}
            value={email || ""}
            onChange={handleEmailChange}
          />
          <Input
            type="password"
            autocomplete="password"
            required
            title="Password:"
            placeholder="Your password"
            state={{
              error: getFieldError('password') ? {
                condition: true,
                text: getFieldError('password'),
              } : null,
              details: !getFieldError('password') ? {
                condition: true,
                text: "Your account password",
              } : null,
            }}
            value={password || ""}
            onChange={handlePasswordChange}
          />

          <button
            type="submit"
            className={`${styles['btn']} ${styles['btn-primary']} mt-1`}
            disabled={auth.loading}
          >
            {auth.loading ? "Logging in..." : "Log in"}
          </button>
          <div className="mt-4">
            <Link to={"/auth/reset-password"} style={{ textDecoration: 'none' }}>Forgot password</Link>
          </div>
          <div className="mt-4">
            <Link to={"/auth/register"} style={{ textDecoration: 'none' }}>No account? Sign up</Link>
          </div>
        </Form>
      </div>
    </>
  );
};
